import { ActionType, EVENT_TYPE, type CalendarEvent } from "@fitness-app/data-models/domain/services/ProgramService";
import { ProgramAutomationTaskStatus } from "@fitness-app/data-models/entities/ProgramAutomation";

import { groupByDay, toDayKey } from "~/components/EventCalendar/calendarModel";

// Kolejność w obrębie dnia: najpierw to, od czego zależy reszta (start planu, trening),
// na końcu dane, które klientka wpisała sama.
const EVENT_ORDER: Record<string, number> = {
  [ActionType.StartOfCurrenPlan]: 0,
  [ActionType.StartOfScheduledPlan]: 0,
  [EVENT_TYPE.WORKOUT]: 1,
  [EVENT_TYPE.CALENDAR_EVENT]: 2,
  [EVENT_TYPE.AUTOMATION_TASK]: 3,
  [EVENT_TYPE.MEASUREMENT]: 4,
  [EVENT_TYPE.MEAL_TRACKED]: 5,
};

export const groupEventsByDay = (events: CalendarEvent[]) => {
  const byDay = groupByDay(events, (event) => event.start);

  for (const dayEvents of byDay.values()) {
    dayEvents.sort(
      (a, b) =>
        (EVENT_ORDER[a.type] ?? 9) - (EVENT_ORDER[b.type] ?? 9) ||
        new Date(a.start).getTime() - new Date(b.start).getTime(),
    );
  }

  return byDay;
};

// `ProgramService` nadaje zadaniom automatyzacji, posiłkom i startowi planu nowe `uuid()` przy każdym
// przeliczeniu. Klucz z takiego id przemontowywał chip w trakcie przeciągania (dnd-kit gubił
// zarejestrowany element) i zamykał otwarty dymek przy każdym renderze.
const getStableEventId = (event: CalendarEvent, index: number) => {
  switch (event.type) {
    case EVENT_TYPE.AUTOMATION_TASK:
      return event.data.id;
    case EVENT_TYPE.MEAL_TRACKED:
    case ActionType.StartOfCurrenPlan:
      return "day";
    default:
      return event.id || String(index);
  }
};

export const getEventKey = (event: CalendarEvent, index = 0) =>
  `${event.type}-${getStableEventId(event, index)}-${toDayKey(event.start)}`;

// Treningi z planu zaplanowanego i wysłane wiadomości i tak nie dałoby się przesunąć,
// więc nie udają, że się da.
export const isDraggableEvent = (event: CalendarEvent) => {
  if (event.type === EVENT_TYPE.WORKOUT) {
    return !event.metadata.readOnly;
  }

  if (event.type === EVENT_TYPE.AUTOMATION_TASK) {
    return event.data.status === ProgramAutomationTaskStatus.Upcoming;
  }

  return false;
};

export const getEventTone = (event: CalendarEvent) => {
  switch (event.type) {
    case EVENT_TYPE.WORKOUT:
      return event.metadata.readOnly
        ? "border-dashed border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
        : "border-indigo-100 bg-indigo-50 text-indigo-950 hover:border-indigo-200 hover:bg-indigo-100";
    case EVENT_TYPE.AUTOMATION_TASK:
      return "border-amber-100 bg-amber-50 text-amber-950 hover:border-amber-200 hover:bg-amber-100";
    case EVENT_TYPE.CALENDAR_EVENT:
      return "border-emerald-100 bg-emerald-50 text-emerald-950 hover:border-emerald-200 hover:bg-emerald-100";
    case EVENT_TYPE.MEASUREMENT:
      return "border-sky-100 bg-sky-50 text-sky-950 hover:border-sky-200 hover:bg-sky-100";
    case EVENT_TYPE.MEAL_TRACKED:
      return "border-rose-100 bg-rose-50 text-rose-950 hover:border-rose-200 hover:bg-rose-100";
    default:
      return "border-blue-100 bg-blue-50 text-blue-950 hover:border-blue-200 hover:bg-blue-100";
  }
};
